import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import "font-awesome/css/font-awesome.min.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Chatbot from './Chatbot';
import { Element } from 'react-scroll';
import axios from 'axios';
import { SERVER_URL } from './constants.js';

// Components
import Header from "./components/Header.js";
import CategoryProducts from "./components/pages/CategoryProducts.js";
import Dcategory from "./components/pages/home/Dcategory.js";
import Hero from "./components/pages/home/Hero/HeroPage.js";
import CategorySection from "./components/pages/home/CategorySection.js";
import ProductDeals from "./components/pages/home/ProductDeals.js";
import TopSellingProducts from "./components/pages/home/TopSellingProducts.js";
import TopProducts from "./components/pages/home/TopProducts.js";
import FeaturedProducts from "./components/pages/home/FeaturedProducts.js";
import HotTrendingProducts from "./components/pages/home/HotTrendingProducts.js";
import BlogPage from "./components/Blog/BlogPage.js";
import Footer from "./components/Footer.js";
import Register from "./components/Register";
import Login from "./components/Login";
import NotFound from "./components/NotFound";
import AllSectors from "./components/AllSectors.js";
import ProductDetails from "./components/pages/ProductDetails";
import ShowMoreProducts from "./components/ShowMoreProducts";
import UserProfile from "./components/User/UserProfile.js";
import CategoryPage from "./components/pages/CategoryPage.js";
import ProductPage from "./components/pages/ProductPage.js";
import Checkout from "./components/pages/Checkout/Checkout.js";
import Cart from "./components/pages/CartPage.js";
import { CartContextProvider } from "./context/CartContext.js";
import { CategoryContextProvider } from "./context/CategoryContext.js";
import Favorites from "./components/pages/Favorites.js";
import { FavoritesProvider } from "./context/FavoritesContext";
import { ClerkProvider, SignedIn, SignedOut, useUser } from "@clerk/clerk-react";
import AboutUs from "./components/pages/AboutUs.js";
import OrderTracking from "./components/OrderTracking.js";
import ContactUs from "./components/pages/ContactUs.js";
import FAQs from "./components/pages/FaqPage.js";
import MainBlog from "./components/MainBlog.js";
import OrderSuccess from "./components/pages/OrderSuccess.js";
import FavPage from "./components/pages/FavPage.js";
import Sponsor from "./components/pages/home/Sponser.js";
import ScrollToTop from "./ScrollToTop.js";

const globalStyles = `
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  html, body {
    width: 100%;
    height: 100%;
    overflow-x: hidden;
  }

  #root {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
  }

  .app-container {
    flex: 1;
    width: 100%;
    max-width: 100vw;
    overflow-x: hidden;
  }

  .page-container {
    width: 100%;
    max-width: 1440px;
    margin: 0 auto;
    padding: 0 1rem;
  }

  @media (min-width: 640px) {
    .page-container {
      padding: 0 2rem;
    }
  }

  @media (min-width: 1024px) {
    .page-container {
      padding: 0 4rem;
    }
  }

  .grid-container {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 1rem;
    width: 100%;
  }

  @media (min-width: 768px) {
    .grid-container {
      gap: 2rem;
    }
  }

  .full-width { width: 100%; }
  .flex-center { display: flex; justify-content: center; align-items: center; }
  .flex-column { display: flex; flex-direction: column; }
  .responsive-padding { padding: 1rem; }
  @media (min-width: 768px) { .responsive-padding { padding: 2rem; } }
  .hero-section { min-height: 60vh; width: 100%; }
  .category-section { padding: 2rem 1rem; }
  .product-grid { 
    display: grid; 
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr)); 
    gap: 1.5rem; 
    padding: 1rem; 
  }
  @media (max-width: 640px) { 
    .product-grid { 
      grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); 
      gap: 1rem; 
    } 
  }
  .Toastify__toast-container { width: 100%; max-width: 360px; padding: 1rem; }
  @media (max-width: 480px) { 
    .Toastify__toast-container { 
      width: 90%; 
      padding: 0.5rem; 
      margin: 0 auto; 
    } 
  }
`;

const clerkFrontendApi = process.env.REACT_APP_CLERK_FRONTEND_API;

// Country data
const countries = [
  { code: '+91', name: 'India', flag: '🇮🇳' },
  { code: '+1', name: 'USA', flag: '🇺🇸' },
  { code: '+44', name: 'UK', flag: '🇬🇧' },
  // Add more countries as needed
];

const UserForm = ({ onComplete }) => {
  const { user } = useUser();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    img_url: "",
    countryCode: "+91" // Default to India
  });
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [step, setStep] = useState(1);
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);

  const cloudName = "dpgf1rkjl";
  const uploadPreset = "unsigned_preset";

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "phone") {
      // Allow only numbers and limit to 10 digits
      const numericValue = value.replace(/\D/g, '').slice(0, 10);
      setFormData(prev => ({ ...prev, [name]: numericValue }));
    } else if (name === "countryCode") {
      setFormData(prev => ({ ...prev, [name]: value }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const getFullPhoneNumber = () => {
    return `${formData.countryCode}${formData.phone}`;
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);

    setUploading(true);
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);
    uploadFormData.append('upload_preset', uploadPreset);

    try {
      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`,
        {
          method: 'POST',
          body: uploadFormData,
        }
      );
      const data = await response.json();
      
      if (data.secure_url) {
        setFormData(prev => ({ ...prev, img_url: data.secure_url }));
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error("Failed to upload image. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const sendOtp = async (e) => {
    e.preventDefault();
    if (!formData.phone || formData.phone.length !== 10) {
      toast.error("Please enter a valid 10-digit phone number.");
      return;
    }

    setSendingOtp(true);
    try {
      const res = await axios.post(`${SERVER_URL}/api/v1/send-otp`, {
        phone: getFullPhoneNumber(),
      });
      if (res.data.success) {
        setOtpSent(true);
        toast.success("OTP sent successfully!");
      }
    } catch (error) {
      console.error("Error sending OTP:", error);
      toast.error("Failed to send OTP. Please try again.");
    } finally {
      setSendingOtp(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    if (!otp) {
      toast.error("Please enter the OTP.");
      return;
    }

    setVerifyingOtp(true);
    try {
      const res = await axios.post(`${SERVER_URL}/api/v1/verify-otp`, {
        phone: getFullPhoneNumber(),
        otp,
      });
      if (res.data.success) {
        setOtpVerified(true);
        toast.success("OTP verified successfully!");
        setStep(step + 1);
      } else {
        toast.error("Invalid OTP. Please try again.");
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
      toast.error("Failed to verify OTP. Please try again.");
    } finally {
      setVerifyingOtp(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!otpVerified) {
      toast.error("Please verify your phone number with OTP first.");
      return;
    }

    try {
      const res = await axios.post(`${SERVER_URL}/api/v1/user`, {
        userId: user.id,
        ...formData,
        phone: getFullPhoneNumber()
      });
      if (res.data.success) {
        toast.success("Profile created successfully!");
        onComplete(res.data.data);
      }
    } catch (error) {
      console.error("Error creating user:", error);
      toast.error("Failed to create profile. Please try again.");
    }
  };

  const nextStep = (e) => {
    e.preventDefault();
    setStep(step + 1);
  };

  const prevStep = (e) => {
    e.preventDefault();
    setStep(step - 1);
  };

  return (
    <div className="fixed inset-0 bg-white flex h-screen w-screen">
      <div className="hidden md:flex md:w-1/2 bg-gradient-to-br from-blue-600 to-indigo-900 relative">
        <div className="absolute inset-0 bg-black opacity-20"></div>
        <img 
          src="https://images.unsplash.com/photo-1573855619003-97b4799dcd8b?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80" 
          alt="Shopping Experience" 
          className="object-cover w-full h-full mix-blend-overlay"
        />
        <div className="absolute inset-0 flex flex-col justify-center items-center text-white p-12">
          <h1 className="text-5xl font-bold mb-6 text-center">Welcome to Your Shopping Experience</h1>
          <p className="text-xl text-center max-w-md">Complete your profile to get personalized recommendations and a seamless shopping journey.</p>
        </div>
      </div>

      <div className="w-full md:w-1/2 flex items-center justify-center p-6 md:p-12 bg-gray-50">
        <div className="w-full max-w-md">
          <div className="mb-8 text-center">
            <h2 className="text-3xl font-bold text-gray-800 mb-2">Create Your Profile</h2>
            <p className="text-gray-600">Let's get to know you better</p>
          </div>

          <div className="mb-6">
            <div className="flex justify-between mb-4">
              {[1, 2, 3].map((stepNumber) => (
                <div key={stepNumber} className="flex flex-col items-center">
                  <div 
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      step >= stepNumber ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
                    } transition-all duration-300 ease-in-out`}
                  >
                    {step > stepNumber ? (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      stepNumber
                    )}
                  </div>
                  <span className={`text-xs mt-1 ${step >= stepNumber ? 'text-blue-600' : 'text-gray-400'}`}>
                    {stepNumber === 1 ? 'Personal' : stepNumber === 2 ? 'Contact' : 'Photo'}
                  </span>
                </div>
              ))}
            </div>
            <div className="w-full bg-gray-200 h-1 rounded-full">
              <div 
                className="bg-blue-600 h-1 rounded-full transition-all duration-300 ease-in-out"
                style={{ width: `${((step - 1) / 2) * 100}%` }}
              ></div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {step === 1 && (
              <div className="space-y-6 transition-all duration-300">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                    placeholder="Enter your first name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                    placeholder="Enter your last name"
                    required
                  />
                </div>
                <button
                  onClick={nextStep}
                  className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-300 transition duration-300 ease-in-out transform hover:-translate-y-1 hover:shadow-lg"
                >
                  Continue
                </button>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-6 transition-all duration-300">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                  <div className="flex">
                    <select
                      name="countryCode"
                      value={formData.countryCode}
                      onChange={handleInputChange}
                      disabled={otpVerified}
                      className="w-24 px-2 py-3 border border-r-0 border-gray-300 rounded-l-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200 bg-white"
                    >
                      {countries.map((country) => (
                        <option key={country.code} value={country.code}>
                          {country.flag} {country.code}
                        </option>
                      ))}
                    </select>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="flex-1 px-4 py-3 border border-l-0 border-gray-300 rounded-r-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                      placeholder="Enter 10-digit phone number"
                      maxLength="10"
                      required
                      disabled={otpVerified}
                    />
                  </div>
                  {formData.phone && formData.phone.length !== 10 && (
                    <p className="text-sm text-red-600 mt-1">
                      Please enter a 10-digit phone number
                    </p>
                  )}
                </div>

                {!otpVerified && (
                  <>
                    {!otpSent ? (
                      <button
                        onClick={sendOtp}
                        disabled={sendingOtp || formData.phone.length !== 10}
                        className={`w-full py-3 px-6 rounded-lg transition duration-300 ease-in-out transform hover:-translate-y-1 hover:shadow-lg ${
                          sendingOtp || formData.phone.length !== 10
                            ? 'bg-gray-400 cursor-not-allowed'
                            : 'bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-300'
                        }`}
                      >
                        {sendingOtp ? 'Sending OTP...' : 'Send OTP'}
                      </button>
                    ) : (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Enter OTP</label>
                          <input
                            type="text"
                            value={otp}
                            onChange={(e) => setOtp(e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                            placeholder="Enter the OTP"
                            required
                          />
                        </div>
                        <button
                          onClick={verifyOtp}
                          disabled={verifyingOtp}
                          className={`w-full py-3 px-6 rounded-lg transition duration-300 ease-in-out transform hover:-translate-y-1 hover:shadow-lg ${
                            verifyingOtp
                              ? 'bg-gray-400 cursor-not-allowed'
                              : 'bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-300'
                          }`}
                        >
                          {verifyingOtp ? 'Verifying OTP...' : 'Verify OTP'}
                        </button>
                      </>
                    )}
                  </>
                )}

                {otpVerified && (
                  <div className="text-sm text-green-600 flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Phone number verified!
                  </div>
                )}

                <div className="flex space-x-4">
                  <button
                    onClick={prevStep}
                    className="w-1/2 bg-gray-200 text-gray-800 py-3 px-6 rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-4 focus:ring-gray-200 transition duration-300 ease-in-out"
                  >
                    Back
                  </button>
                  {otpVerified && (
                    <button
                      onClick={nextStep}
                      className="w-1/2 bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-300 transition duration-300 ease-in-out transform hover:-translate-y-1 hover:shadow-lg"
                    >
                      Continue
                    </button>
                  )}
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-6 transition-all duration-300">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Profile Image</label>
                  <div className="flex flex-col items-center space-y-4">
                    <div className={`w-32 h-32 rounded-full border-2 ${imagePreview ? 'border-blue-500' : 'border-dashed border-gray-300'} flex items-center justify-center overflow-hidden bg-gray-100`}>
                      {imagePreview ? (
                        <img
                          src={imagePreview}
                          alt="Preview"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      )}
                    </div>
                    <label htmlFor="image-upload" className="cursor-pointer">
                      <div className="mt-2 flex items-center justify-center w-full">
                        <span className="py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                          {imagePreview ? 'Change Photo' : 'Upload Photo'}
                        </span>
                        <input
                          id="image-upload"
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          className="hidden"
                        />
                      </div>
                    </label>
                    {uploading && (
                      <div className="text-sm text-blue-600 flex items-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Uploading image...
                      </div>
                    )}
                    {formData.img_url && !uploading && (
                      <div className="text-sm text-green-600 flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Image uploaded successfully!
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex space-x-4 pt-4">
                  <button
                    onClick={prevStep}
                    className="w-1/2 bg-gray-200 text-gray-800 py-3 px-6 rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-4 focus:ring-gray-200 transition duration-300 ease-in-out"
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={uploading}
                    className={`w-1/2 py-3 px-6 rounded-lg transition duration-300 ease-in-out transform hover:-translate-y-1 hover:shadow-lg ${
                      uploading 
                        ? 'bg-gray-400 cursor-not-allowed' 
                        : 'bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-300'
                    }`}
                  >
                    {uploading ? 'Please wait...' : 'Complete Setup'}
                  </button>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

const AppContent = () => {
  const { user } = useUser();
  const [userDetails, setUserDetails] = useState(null);
  const [showUserForm, setShowUserForm] = useState(false);
  const [categoryname, setcategoryname] = useState();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkUser = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const res = await axios.get(`${SERVER_URL}/api/v1/user/${user.id}`);
        
        if (res.data.success && res.data.data) {
          setUserDetails(res.data.data);
          setShowUserForm(false);
        } else {
          setUserDetails(null);
          setShowUserForm(true);
        }
      } catch (error) {
        if (error.response?.status === 404) {
          setUserDetails(null);
          setShowUserForm(true);
        } else {
          console.error("Error checking user:", error);
          setShowUserForm(false);
        }
      } finally {
        setLoading(false);
      }
    };

    checkUser();
  }, [user?.id]);

  const handleUserFormComplete = (userData) => {
    setUserDetails(userData);
    setShowUserForm(false);
  };

  const cn = (e) => {
    setcategoryname(e);
  };

  if (showUserForm) {
    return <UserForm onComplete={handleUserFormComplete} />;
  }

  return (
    <div className="app-container">
      <Header cn={cn} />
      <main className="main-content">
        <ScrollToTop />
        <Routes>
          <Route
            path="/"
            element={
              <>
                <Hero />
                <div className="page-container">
                  <ProductDeals />
                  <TopSellingProducts />
                  <FeaturedProducts />
                  <HotTrendingProducts />
                  <Sponsor />
                </div>
              </>
            }
          />
          <Route path="/cart" element={<div className="page-container"><Cart /></div>} />
          <Route path="/categories" element={<div className="page-container"><AllSectors /></div>} />
          <Route path="/categories/:categoryName" element={<CategoryProducts />} />
          <Route
            path="/checkout"
            element={
              <div className="page-container">
                <SignedIn>
                  <Checkout />
                </SignedIn>
                <SignedOut>
                  <Navigate to="/login" />
                </SignedOut>
              </div>
            }
          />
          <Route
            path="/order/successfull/:orderId"
            element={
              <div className="page-container">
                <SignedIn>
                  <OrderSuccess />
                </SignedIn>
                <SignedOut>
                  <Navigate to="/login" />
                </SignedOut>
              </div>
            }
          />
          <Route path="/products" element={<div className="page-container"><ShowMoreProducts /></div>} />
          <Route path="/product/:productId" element={<div className="page-container"><ProductDetails /></div>} />
          <Route
            path="/profile"
            element={
              <div className="page-container">
                <SignedIn>
                  <UserProfile />
                </SignedIn>
                <SignedOut>
                  <Navigate to="/login" />
                </SignedOut>
              </div>
            }
          />
          <Route path="/dcategory" element={<div className="page-container"><Dcategory /></div>} />
          <Route path="/about" element={<div className="page-container"><AboutUs /></div>} />
          <Route path="/order-tracking" element={<div className="page-container"><OrderTracking /></div>} />
          <Route path="/contact" element={<div className="page-container"><ContactUs /></div>} />
          <Route path="/faqs" element={<div className="page-container"><FAQs /></div>} />
          <Route path="/blog" element={<div className="page-container"><BlogPage /></div>} />
          <Route path="/register" element={<div className="page-container"><Register /></div>} />
          <Route path="/login" element={<div className="page-container"><Login /></div>} />
          <Route path="/favorites" element={<div className="page-container"><FavPage /></div>} />
          <Route path="*" element={<div className="page-container"><NotFound /></div>} />
        </Routes>
      </main>
      <ToastContainer
        position="bottom-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        className="toast-container"
      />
      <Footer />
      <Chatbot />
    </div>
  );
};

const App = () => {
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.innerHTML = globalStyles;
    document.head.appendChild(styleElement);
    return () => document.head.removeChild(styleElement);
  }, []);

  return (
    <ClerkProvider publishableKey={clerkFrontendApi}>
      <CategoryContextProvider>
        <CartContextProvider>
          <FavoritesProvider>
            <AppContent />
          </FavoritesProvider>
        </CartContextProvider>
      </CategoryContextProvider>
    </ClerkProvider>
  );
};

export default App;
